'use client'

import Link from 'next/link'
import { useState } from 'react'
import { Sun, Moon, Menu, X } from 'lucide-react'
import { useTheme } from './ThemeProvider'
import StoreBadges from './StoreBadges'

const navLinks = [
  { href: '/horoscope', label: 'Daily Horoscope' },
  { href: '/tarot', label: 'Tarot Reading' },
  { href: '/#tools', label: 'Free Tools' },
  { href: '/#faq', label: 'FAQ' },
  { href: '/blog', label: 'Blog' },
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/terms-and-conditions', label: 'Terms & Conditions' },
]

export default function Navbar() {
  const { theme, toggleTheme } = useTheme()
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'var(--nav-bg)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>

      {/* Main nav */}
      <nav style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', gap: '8px', height: '68px' }}>

        {/* Logo */}
        <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '32px', textDecoration: 'none', flexShrink: 0 }} aria-label="SarvamAstro home">
          {/* 96px asset for a 36px slot (sharp at 2x retina) — far
              smaller than the old 512px PNG, removes it from the LCP path. */}
          <img
            src="/images/logos/favicon-96x96.png"
            alt="SarvamAstro"
            width={36}
            height={36}
            decoding="async"
            fetchPriority="high"
            style={{ objectFit: 'contain', borderRadius: '50%' }}
          />
          <span style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            Sarvam<span style={{ color: 'var(--accent)' }}>Astro</span>
          </span>
        </Link>

        {/* Desktop nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }} className="hidden-mobile">
          {navLinks.map(link => (
            <Link key={link.label} href={link.href} className="nav-pill" style={{ textDecoration: 'none' }}>
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.3s', flexShrink: 0, color: 'var(--text-primary)'
            }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* App store buttons — desktop. flexShrink:0 + wrap=false keeps
              the two badges side-by-side instead of stacking when the
              nav row gets tight. */}
          <div className="hidden-mobile" style={{ flexShrink: 0 }}>
            <StoreBadges direction="row" height={38} wrap={false} />
          </div>

          {/* Mobile menu btn */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              width: 40, height: 40, borderRadius: '8px',
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              cursor: 'pointer', display: 'none', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-primary)', flexShrink: 0
            }}
            id="mobile-menu-btn"
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div style={{
          background: 'var(--nav-bg)', borderTop: '1px solid var(--border)',
          padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '8px'
        }}>
          {navLinks.map(link => (
            <Link
              key={link.label}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              style={{ color: 'var(--text-secondary)', textDecoration: 'none', padding: '10px 0', fontSize: '15px', fontWeight: 500, borderBottom: '1px solid var(--border)' }}
            >
              {link.label}
            </Link>
          ))}
          {/* Mobile app store badges */}
          <div style={{ paddingTop: '12px' }}>
            <StoreBadges direction="row" height={40} />
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .hidden-mobile { display: none !important; }
          #mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </header>
  )
}
